import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, ReactNode } from 'react';

interface DarkThemeContextType {
  isDarkTheme: boolean;
  toggleDarkTheme: () => void;
}

const DarkThemeContext = createContext<DarkThemeContextType | undefined>(undefined);

const DARK_THEME_STORAGE_KEY = 'api-hub-dark-theme';
const DARK_COBALT_LINK_ID = 'cobalt-dark-theme-link';

function loadDarkCobaltStyles() {
  if (document.getElementById(DARK_COBALT_LINK_ID)) return;

  // Dynamically inject the Cobalt dark core CSS as a <link> tag.
  // Source: @msi/cobalt-styles/cobalt-dark-core.css (copied to public/ via postinstall script).
  // This overrides the light :root CSS custom properties with dark values.
  // It cannot be imported statically because it targets :root unconditionally.
  const link = document.createElement('link');
  link.id = DARK_COBALT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = '/cobalt-dark-core.css';
  document.head.appendChild(link);
}

function unloadDarkCobaltStyles() {
  const link = document.getElementById(DARK_COBALT_LINK_ID);
  if (link) {
    link.remove();
  }
}

function applyThemeToDocument(dark: boolean) {
  if (dark) {
    document.documentElement.setAttribute('data-theme', 'dark');
    loadDarkCobaltStyles();
  } else {
    document.documentElement.removeAttribute('data-theme');
    unloadDarkCobaltStyles();
  }
}

interface DarkThemeProviderProps {
  children: ReactNode;
}

export const DarkThemeProvider: React.FC<DarkThemeProviderProps> = ({ children }) => {
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(() => {
    const stored = localStorage.getItem(DARK_THEME_STORAGE_KEY);
    return stored === 'true';
  });

  useLayoutEffect(() => {
    applyThemeToDocument(isDarkTheme);
  }, [isDarkTheme]);

  useEffect(() => {
    localStorage.setItem(DARK_THEME_STORAGE_KEY, JSON.stringify(isDarkTheme));
  }, [isDarkTheme]);

  const toggleDarkTheme = useCallback(() => {
    setIsDarkTheme(prev => !prev);
  }, []);

  const value: DarkThemeContextType = {
    isDarkTheme,
    toggleDarkTheme,
  };

  return (
    <DarkThemeContext.Provider value={value}>
      {children}
    </DarkThemeContext.Provider>
  );
};

export const useDarkTheme = (): DarkThemeContextType => {
  const context = useContext(DarkThemeContext);
  if (context === undefined) {
    throw new Error('useDarkTheme must be used within a DarkThemeProvider');
  }
  return context;
};
